import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from '@/context/AuthContext'
import { History, CheckCircle2, CheckSquare, FolderKanban, Building2, Loader2 } from 'lucide-react'
import { DEPARTMENTS, deptColor } from '@/lib/departments'
import { hasCrossDeptView } from '@/lib/roles'
import { RecurrenceBadge } from '@/components/RecurrenceControl'
import {
  type CompletionItem,
  markSeen,
  NOTIF_CHANGED_EVENT,
} from '@/lib/completions'

const FALLBACK_DEPT = 'Lainnya'
const WINDOW_DAYS = 7
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000

// Pantau kelas 'dark' di <html> agar warna inline (departemen) bisa menyesuaikan.
function useDark(): boolean {
  const [dark, setDark] = useState(false)
  useEffect(() => {
    const el = document.documentElement
    const update = () => setDark(el.classList.contains('dark'))
    update()
    const obs = new MutationObserver(update)
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return dark
}

// Warna teks departemen versi TERANG untuk dark mode (deptColor.text terlalu gelap).
const DEPT_DARK_TEXT: Record<string, string> = {
  'HOLDING':     '#D6D5CF',
  'HAI-X':       '#C4B5FD',
  'PADEL PARTY': '#93C5FD',
  'UIM':         '#FCD34D',
  'UKP':         '#A5B4FC', // navy (versi terang)
  'DPP':         '#BFDBFE', // biru muda (versi terang)
}

interface DeptVisual { bg: string; text: string; border: string; badgeBg: string; badgeText: string }
// Warna departemen yang sadar tema (header kolom, chip, badge hitung).
function deptVisual(dept: string | null, dark: boolean): DeptVisual {
  const c = deptColor(dept)
  if (!dark) return { bg: c.bg, text: c.text, border: c.border, badgeBg: c.text, badgeText: c.bg }
  const t = DEPT_DARK_TEXT[dept ?? ''] ?? '#A4A29C'
  return { bg: '#26242C', text: t, border: '#34333C', badgeBg: t, badgeText: '#1F1E25' }
}

// Waktu relatif singkat (id). Fallback ke tanggal utk yang lama.
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  const diff = Date.now() - then
  if (Number.isNaN(then)) return ''
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'baru saja'
  if (m < 60) return `${m} mnt lalu`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} jam lalu`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d} hari lalu`
  return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
}

const KIND_BADGE: Record<CompletionItem['kind'], { label: string; cls: string }> = {
  task:    { label: 'Task',    cls: 'bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]' },
  subtask: { label: 'Subtask', cls: 'bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE]' },
}

function LogCard({ item, dark }: { item: CompletionItem; dark: boolean }) {
  const dc = deptVisual(item.department, dark)
  const kb = KIND_BADGE[item.kind]
  return (
    <div className="rounded-xl border border-[#E8E7E3] bg-white p-3 shadow-sm hover:shadow transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
          <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${kb.cls}`}>
            {item.kind === 'task' ? <CheckCircle2 className="w-2.5 h-2.5" /> : <CheckSquare className="w-2.5 h-2.5" />}
            {kb.label}
          </span>
          {item.recurrence && <RecurrenceBadge value={item.recurrence} size="xs" />}
        </div>
        <span className="text-[10px] text-[#AEADA8] font-body whitespace-nowrap flex-shrink-0">{timeAgo(item.completedAt)}</span>
      </div>

      <p className="text-[12.5px] font-body text-[#1A1916] leading-snug">
        <span className="font-semibold">{item.actorName ?? 'Seseorang'}</span>{' '}
        <span className="text-[#6B6860]">menyelesaikan</span>
      </p>
      <p className="text-[13px] font-body font-semibold text-[#1A1916] leading-snug mt-0.5 break-words">
        {item.title}
      </p>
      {item.kind === 'subtask' && item.parentTitle && (
        <p className="text-[10.5px] text-[#AEADA8] font-body leading-snug truncate">
          dalam task “{item.parentTitle}”
        </p>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10.5px] text-[#6B6860] font-body min-w-0">
          <FolderKanban className="w-3 h-3 flex-shrink-0 text-[#8A8A85]" />
          <span className="truncate">{item.projectName ?? '—'}</span>
        </span>
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wider border whitespace-nowrap"
          style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}
        >
          <Building2 className="w-2.5 h-2.5" />
          {item.department ?? FALLBACK_DEPT}
        </span>
      </div>
    </div>
  )
}

export default function HistoryLogPage() {
  const [items, setItems] = useState<CompletionItem[]>([])
  const [loading, setLoading] = useState(true)
  const dark = useDark()
  const { data: session } = useSession()
  const myDept = session?.user?.department ?? null
  const isHolding = hasCrossDeptView(session?.user ?? {})

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/completions')
      if (res.ok) {
        const d = await res.json()
        setItems(Array.isArray(d.items) ? d.items : [])
      }
    } catch {
      /* abaikan */
    } finally {
      setLoading(false)
      markSeen() // sedang melihat halaman → reset badge
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Realtime: badge sidebar (punya koneksi SSE) me-relay event saat ada
  // perubahan. Plus polling 30s sebagai jaring pengaman.
  useEffect(() => {
    const onChanged = () => { void load() }
    window.addEventListener(NOTIF_CHANGED_EVENT, onChanged)
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 30000)
    return () => {
      window.removeEventListener(NOTIF_CHANGED_EVENT, onChanged)
      clearInterval(t)
    }
  }, [load])

  // Hanya tampilkan penyelesaian dalam 7 hari terakhir (per departemen).
  const recentItems = useMemo(() => {
    const cutoff = Date.now() - WINDOW_MS
    return items.filter((it) => {
      const t = new Date(it.completedAt).getTime()
      return !Number.isNaN(t) && t >= cutoff
    })
  }, [items])

  // Kolom: Director & anggota HOLDING melihat SEMUA departemen; selain itu hanya
  // departemennya sendiri (data dari API juga sudah di-scope sama).
  const columns = useMemo(() => {
    if (!isHolding) return myDept ? [myDept] : []
    const present = new Set(recentItems.map((i) => i.department || FALLBACK_DEPT))
    const base = [...DEPARTMENTS] as string[]
    const extras = [...present].filter((d) => !base.includes(d)).sort()
    return [...base, ...extras]
  }, [recentItems, isHolding, myDept])

  const grouped = useMemo(() => {
    const map = new Map<string, CompletionItem[]>()
    for (const it of recentItems) {
      const key = it.department || FALLBACK_DEPT
      const arr = map.get(key) ?? []
      arr.push(it)
      map.set(key, arr)
    }
    return map
  }, [recentItems])

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-3" style={{ height: '52px' }}>
          <History className="w-4 h-4 text-[#6366F1]" />
          <span className="font-display text-[15px] font-bold text-[#1A1916]">History Log</span>
          <span className="text-[11px] text-[#8A8A85] font-body hidden sm:inline">
            Penyelesaian task &amp; subtask antar departemen · 7 hari terakhir
          </span>
          {!loading && (
            <span className="ml-auto text-[11px] font-body text-[#6B6860]">
              {recentItems.length} penyelesaian · 7 hari terakhir
            </span>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#8A8A85]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat…
        </div>
      ) : (
        <div className="p-4 sm:p-6">
          {/* Kanban: SATU baris, kolom dibagi rata (1fr) — semua dept muat, tiap kolom scroll sendiri */}
          <div className="grid gap-3 items-start" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
            {columns.map((dept) => {
              const list = grouped.get(dept) ?? []
              const dv = deptVisual(dept === FALLBACK_DEPT ? null : dept, dark)
              return (
                <section
                  key={dept}
                  className="min-w-0 rounded-2xl bg-[#FAFAF8] border border-[#EDECE7] flex flex-col"
                  style={{ maxHeight: 'calc(100vh - 120px)' }}
                >
                  {/* Kolom header */}
                  <div
                    className="flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-t-2xl border-b"
                    style={{ backgroundColor: dv.bg, borderColor: dv.border }}
                  >
                    <span className="inline-flex items-center gap-1.5 font-body font-bold text-[12px] uppercase tracking-wider" style={{ color: dv.text }}>
                      <Building2 className="w-3.5 h-3.5" />
                      {dept}
                    </span>
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center"
                      style={{ backgroundColor: dv.badgeBg, color: dv.badgeText }}
                    >
                      {list.length}
                    </span>
                  </div>

                  {/* Kolom body */}
                  <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5">
                    {list.length === 0 ? (
                      <p className="text-center text-[11px] text-[#B5B4AF] font-body py-8">
                        Belum ada penyelesaian (7 hari terakhir)
                      </p>
                    ) : (
                      list.map((it) => <LogCard key={it.id} item={it} dark={dark} />)
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
